import { useAuth, useUser } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { useAppDispatch } from '../hooks';
import { logger } from '../lib/logger';
import {
  clerkToEZHOAUser,
  setEZHOAUser,
  setError,
  setLoading,
} from '../store/slices/clerkAuthSlice';
import { fetchNotificationsFromDB } from '../store/thunks/notificationThunks';

/**
 * Component that syncs Clerk authentication state with Redux and Cloudflare D1 database
 * This ensures our Redux store always has the latest user data
 *
 * Updated: 2025-10-20 - Now uses Cloudflare Pages Functions API for user sync
 */
export const ClerkAuthSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const dispatch = useAppDispatch();

  useEffect(() => {
    async function syncUser() {
      if (!isLoaded) {
        dispatch(setLoading(true));
        return;
      }

      if (isSignedIn && user) {
        // Convert Clerk user to EZHOA user format
        const ezhoaUser = clerkToEZHOAUser(user);
        // Set EZHOA user in Redux
        dispatch(setEZHOAUser(ezhoaUser));

        // Sync user with Cloudflare D1 database via API
        try {
          // Get authentication token from Clerk
          const token = await getToken();

          if (!token) {
            console.error('Failed to get authentication token from Clerk');
            return;
          }

          // Get API base URL from environment
          const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

          // Call API to sync user
          const response = await fetch(`${apiUrl}/api/user/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: user.emailAddresses[0]?.emailAddress || '',
              name: user.fullName || user.firstName || 'User',
              phone: user.phoneNumbers[0]?.phoneNumber || undefined,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              `User sync failed: ${response.status} ${errorData.error || response.statusText}`
            );
          }

          const data = await response.json();
          logger.log('User synced successfully:', data);

          // Load notifications after user is synced
          dispatch(fetchNotificationsFromDB());
        } catch (error) {
          console.error('Failed to sync user with database:', error);
          dispatch(
            setError(error instanceof Error ? error.message : 'Failed to sync user data')
          );
        }
      } else {
        // User is signed out
        logger.log('User signed out, clearing EZHOA user');
        dispatch(setEZHOAUser(null));
      }
    }

    syncUser();
  }, [user, isLoaded, isSignedIn, getToken, dispatch]);

  // Handle sign out errors
  useEffect(() => {
    if (!isLoaded) return;

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('Clerk')) {
        dispatch(setError(event.reason.message));
      }
    };

    // Listen for Clerk errors
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isLoaded, dispatch]);

  return <>{children}</>;
};
